import { AlertCircle, ArrowLeft, Check, ChevronRight, MapPin, Users } from 'lucide-react';
import { PaymentReturnNotice, readHostedCheckoutReturnState } from '@triedge/payments';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { HostedPaymentSessionInput } from '@triedge/domain-core';
import {
  useGetParentRegistrationQuery,
  useSaveRegistrationDraftMutation,
  useSubmitRegistrationMutation,
} from '../../../state/domainRouterApi';
import { createHostedPaymentSession } from '../../../utils/payments';
import { resolveCognitoProfile } from '../../../utils/cognito-profile';
import { SupportErrorMessage } from '../../../components/overlays/EngineeringSupportBanner';
import { mapParentRegistrationResponse } from '../camps/viewModels';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type EligibleStudent = {
  studentId?: string;
  name?: string;
  grade?: string;
};

type CampSession = {
  sessionId?: string;
  label?: string;
  startDate?: string;
  endDate?: string;
  capacity?: number;
  enrolled?: number;
  spotsRemaining?: number;
};

type PricingLine = {
  label?: string;
  amountCents?: number;
};

type PricingSummary = {
  lines?: PricingLine[];
  totalCents?: number;
};

type ParentCampRegistrationResponse = {
  familyId?: string;
  familyName?: string;
  program?: {
    programId?: string;
    name?: string;
    campType?: string;
    location?: string;
    gradeRange?: string;
    pricePerSessionCents?: number;
  };
  sessions?: CampSession[];
  eligibleStudents?: EligibleStudent[];
  draft?: {
    id?: string;
    selections?: Array<{
      studentId?: string;
      sessionIds?: string[];
    }>;
    waiverAccepted?: boolean;
  };
  pricingSummary?: PricingSummary;
  paymentReadiness?: {
    ready?: boolean;
    reason?: string;
  };
};

type CampRegistrationListResponse = {
  items?: Array<{
    id?: string;
    studentName?: string;
    grossCents?: number;
    amountPaidCents?: number;
    refundAmountCents?: number;
  }>;
};

type WizardStep = 'campers' | 'schedule' | 'review';

const resolveCheckoutUrl = (kind: 'success' | 'cancel', programId: string): string => {
  const url = new URL(window.location.href);
  url.pathname = kind === 'success'
    ? '/parent/portal/camps/registrations'
    : `/parent/portal/camps/${programId}/register`;
  url.search = '';
  url.searchParams.set('checkout', kind);
  url.searchParams.set('programId', programId);
  return url.toString();
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(cents: number | null | undefined): string {
  if (cents == null) return '$0';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

const STEPS: { key: WizardStep; label: string; step: number }[] = [
  { key: 'campers', label: 'Select Campers', step: 1 },
  { key: 'schedule', label: 'Choose Schedule', step: 2 },
  { key: 'review', label: 'Review & Pay', step: 3 },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-6 w-32 animate-pulse rounded bg-gray-100" />
      <div className="h-28 animate-pulse rounded-xl bg-gray-100" />
      <div className="h-10 animate-pulse rounded-lg bg-gray-100" />
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-xl bg-gray-100" />
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ title, detail }: { title: string; detail: string }) {
  return (
    <SupportErrorMessage>
      <p className="font-semibold text-irongrey">{title}</p>
      <p className="mt-1 text-sm text-irongrey/65">{detail}</p>
    </SupportErrorMessage>
  );
}

function Stepper({ current }: { current: WizardStep }) {
  const currentIdx = STEPS.findIndex((s) => s.key === current);

  return (
    <nav data-testid="camps-register-stepper" className="flex items-center gap-2" aria-label="Registration steps">
      {STEPS.map((step, idx) => {
        const isComplete = idx < currentIdx;
        const isActive = idx === currentIdx;
        return (
          <div key={step.key} className="flex items-center gap-2">
            {idx > 0 ? (
              <ChevronRight className="h-4 w-4 text-text-secondary" aria-hidden="true" />
            ) : null}
            <div className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold ${
                  isComplete
                    ? 'bg-[var(--aquamarine)] text-white'
                    : isActive
                      ? 'bg-[var(--primary)] text-white'
                      : 'border border-border bg-white text-text-secondary'
                }`}
              >
                {isComplete ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : step.step}
              </span>
              <span
                className={`text-sm font-medium ${
                  isActive ? 'text-iron-grey' : 'text-text-secondary'
                }`}
              >
                {step.label}
              </span>
            </div>
          </div>
        );
      })}
    </nav>
  );
}

function PricingSidebar({ summary }: { summary: PricingSummary | undefined }) {
  if (!summary) return null;

  return (
    <aside
      data-testid="camps-pricing-sidebar"
      className="rounded-xl border border-border bg-white p-5 shadow-sm"
    >
      <h3 className="mb-4 text-sm font-semibold uppercase tracking-[0.05em] text-text-secondary">
        Pricing Summary
      </h3>
      <div className="space-y-2">
        {(summary.lines ?? []).map((line, idx) => (
          <div key={line.label ?? idx} className="flex items-center justify-between text-sm">
            <span className="text-text-secondary">{line.label ?? '--'}</span>
            <span className="font-medium text-iron-grey">{formatCurrency(line.amountCents)}</span>
          </div>
        ))}
      </div>
      {summary.totalCents != null ? (
        <div className="mt-4 border-t border-border pt-4">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-iron-grey">Total</span>
            <span className="text-lg font-semibold text-iron-grey">
              {formatCurrency(summary.totalCents)}
            </span>
          </div>
        </div>
      ) : null}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Page Component
// ---------------------------------------------------------------------------

export function ParentCampsRegisterPage() {
  const location = useLocation();
  const { campId } = useParams<{ campId: string }>();
  const navigate = useNavigate();
  const programId = campId ?? '';

  const { data: rawData, isLoading, isError, error } = useGetParentRegistrationQuery(
    { programId },
    { skip: !programId },
  );
  const [submitRegistration, { isLoading: isSubmitting }] = useSubmitRegistrationMutation();
  const [saveRegistrationDraft, { isLoading: isSavingDraft }] = useSaveRegistrationDraftMutation();
  const [draftSaved, setDraftSaved] = useState(false);

  const data = mapParentRegistrationResponse(rawData) as ParentCampRegistrationResponse;
  const draftSelectionStudentIds = useMemo(
    () => (data.draft?.selections ?? [])
      .map((selection) => selection.studentId ?? '')
      .filter(Boolean),
    [data.draft?.selections],
  );
  const draftSelectionSessionIds = useMemo(
    () => Array.from(
      new Set(
        (data.draft?.selections ?? [])
          .flatMap((selection) => selection.sessionIds ?? [])
          .filter(Boolean),
      ),
    ),
    [data.draft?.selections],
  );

  // Wizard state
  const [currentStep, setCurrentStep] = useState<WizardStep>('campers');
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(new Set());
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const program = data.program;
  const sessions = data.sessions ?? [];
  const eligibleStudents = data.eligibleStudents ?? [];

  const toggleStudent = useCallback((id: string) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const toggleSession = useCallback((id: string) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const estimatedTotalCents = useMemo(() => {
    if (data.pricingSummary?.totalCents != null) return data.pricingSummary.totalCents;
    const perSession = program?.pricePerSessionCents ?? 0;
    return selectedStudentIds.size * selectedSessionIds.size * perSession;
  }, [data.pricingSummary, program, selectedStudentIds.size, selectedSessionIds.size]);

  useEffect(() => {
    setDraftId(data.draft?.id ?? null);
    setSelectedStudentIds(new Set(draftSelectionStudentIds));
    setSelectedSessionIds(new Set(draftSelectionSessionIds));
    setWaiverAccepted(data.draft?.waiverAccepted ?? false);
  }, [data.draft?.id, data.draft?.waiverAccepted, draftSelectionSessionIds, draftSelectionStudentIds]);

  const buildSelections = useCallback(() => {
    const sessionIds = [...selectedSessionIds];
    return [...selectedStudentIds].map((studentId) => ({
      studentId,
      sessionIds,
    }));
  }, [selectedSessionIds, selectedStudentIds]);

  const canAdvanceFromCampers = selectedStudentIds.size > 0;
  const canAdvanceFromSchedule = selectedSessionIds.size > 0;
  const canSubmit = (data.paymentReadiness?.ready ?? true) && waiverAccepted && canAdvanceFromCampers && canAdvanceFromSchedule;

  const handleSubmit = async () => {
    setSubmitError(null);
    try {
      const selections = buildSelections();
      if (selections.length === 0) {
        throw new Error('Select at least one camper and one session before submitting.');
      }

      const savedDraft = await saveRegistrationDraft({
        draftId: draftId ?? undefined,
        programId,
        selections,
        waiverAccepted,
      }).unwrap();
      const nextDraftId = savedDraft && typeof savedDraft === 'object' && 'id' in savedDraft
        ? String((savedDraft as { id?: unknown }).id ?? '').trim()
        : '';
      if (!nextDraftId) {
        throw new Error('Camp registration draft did not return an id.');
      }
      setDraftId(nextDraftId);

      const submission = await submitRegistration({
        draftId: nextDraftId,
      }).unwrap() as CampRegistrationListResponse;
      const registrations = Array.isArray(submission.items) ? submission.items : [];
      const amountDueCents = registrations.reduce((sum, registration) => (
        sum
        + Math.max(
          0,
          Number(registration.grossCents ?? 0)
          - Number(registration.amountPaidCents ?? 0)
          - Number(registration.refundAmountCents ?? 0),
        )
      ), 0);

      if (registrations.length === 0 || amountDueCents <= 0) {
        navigate('/parent/portal/camps/registrations');
        return;
      }

      const payableRegistrations = registrations.filter((registration) => Math.max(
        0,
        Number(registration.grossCents ?? 0)
        - Number(registration.amountPaidCents ?? 0)
        - Number(registration.refundAmountCents ?? 0),
      ) > 0);
      const registrationIds = payableRegistrations
        .map((registration) => String(registration.id ?? '').trim())
        .filter(Boolean);
      const profile = resolveCognitoProfile();
      const lineItems: HostedPaymentSessionInput['lineItems'] = payableRegistrations.map((registration, index) => ({
        name: `${program?.name ?? 'Camp'} • ${String(registration.studentName ?? `Camper ${index + 1}`).trim() || `Camper ${index + 1}`}`,
        description: 'Camp registration payment',
        quantity: 1,
        unitAmountCents: Math.max(
          0,
          Number(registration.grossCents ?? 0)
          - Number(registration.amountPaidCents ?? 0)
          - Number(registration.refundAmountCents ?? 0),
        ),
      }));
      const checkoutUrl = await createHostedPaymentSession({
        moduleId: 'camps',
        operationKey: 'camps_registration_checkout',
        mode: 'payment',
        currency: 'usd',
        successUrl: resolveCheckoutUrl('success', programId),
        cancelUrl: resolveCheckoutUrl('cancel', programId),
        customerEmail: profile?.email,
        customerReferenceId: profile?.userSub,
        description: `${program?.name ?? 'Camp'} registration checkout`,
        lineItems,
        metadata: {
          route: '/parent/portal/camps/registrations',
          family_id: data.familyId ?? '',
          program_id: programId,
          program_name: program?.name ?? '',
          registration_ids: registrationIds.join(','),
          actorSub: profile?.userSub ?? '',
        },
      });
      window.location.assign(checkoutUrl);
    } catch (err) {
      const msg =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message?: unknown }).message)
          : 'Registration failed. Please try again.';
      setSubmitError(msg);
    }
  };

  // Gap 8: Draft save handler
  const handleSaveDraft = async () => {
    try {
      const selections = buildSelections();
      if (selections.length === 0) {
        return;
      }
      const savedDraft = await saveRegistrationDraft({
        draftId: draftId ?? undefined,
        programId,
        selections,
        waiverAccepted,
      }).unwrap();
      const nextDraftId = savedDraft && typeof savedDraft === 'object' && 'id' in savedDraft
        ? String((savedDraft as { id?: unknown }).id ?? '').trim()
        : '';
      if (nextDraftId) {
        setDraftId(nextDraftId);
      }
      setDraftSaved(true);
      setTimeout(() => setDraftSaved(false), 3000);
    } catch {
      // Draft save failure is non-blocking
    }
  };

  const canSaveDraft = buildSelections().length > 0;
  const hostedReturnState = useMemo(
    () => readHostedCheckoutReturnState(location.search),
    [location.search],
  );

  // Derive selected camper names for family context bar
  const selectedCamperNames = useMemo(
    () =>
      eligibleStudents
        .filter((s) => selectedStudentIds.has(s.studentId ?? ''))
        .map((s) => s.name ?? '--'),
    [eligibleStudents, selectedStudentIds],
  );

  // -----------------------------------------------------------------------
  // Loading / Error states
  // -----------------------------------------------------------------------

  if (isLoading) {
    return (
      <main
        data-testid="parent-camps-register"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <LoadingSkeleton />
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main
        data-testid="parent-camps-register"
        data-route-ready="true"
        data-extracted-from="source-faithful:parent-camps-v1.0"
        className="bg-background-light"
      >
        <div className="w-full space-y-6">
          <NavLink
            to="/parent/portal/camps"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--primary)] hover:underline"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to catalog
          </NavLink>
          <ErrorBanner
            title="Unable to load registration"
            detail={
              (error && typeof error === 'object' && 'message' in error
                ? String((error as { message?: unknown }).message)
                : null) ??
              'Registration data is not available right now. Please try again later.'
            }
          />
        </div>
      </main>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <main
      data-testid="parent-camps-register"
      data-route-ready="true"
      data-extracted-from="source-faithful:parent-camps-v1.0"
      className="bg-background-light"
    >
      <div className="w-full space-y-6">
        <PaymentReturnNotice
          state={hostedReturnState === 'cancel' ? 'cancel' : null}
          noticeKey="parent-camps-register"
          successTitle=""
          successBody=""
          cancelTitle="Camp checkout was cancelled"
          cancelBody="No camp payment was finalized. Your registration selections are still available below."
          guidanceBody="Your camper and session selections stay on this form until you leave the page. Review the registration details below and restart checkout only when the selections are correct."
        />
        {/* Back link */}
        <NavLink
          to="/parent/portal/camps"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--primary)] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to catalog
        </NavLink>

        {/* Camp header card */}
        <div
          data-testid="camps-register-header"
          className="rounded-xl border border-border bg-white p-5 shadow-sm"
        >
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.16em] text-text-secondary">
            Camp Registration
          </p>
          <h1 className="text-[24px] font-semibold text-iron-grey">{program?.name ?? 'Camp Registration'}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-secondary">
            {program?.campType ? <span>{program.campType}</span> : null}
            {program?.location ? (
              <span className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                {program.location}
              </span>
            ) : null}
            {program?.gradeRange ? (
              <span className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5" aria-hidden="true" />
                {program.gradeRange}
              </span>
            ) : null}
            {program?.pricePerSessionCents != null ? (
              <span className="font-medium text-iron-grey">
                {formatCurrency(program.pricePerSessionCents)} / session
              </span>
            ) : null}
          </div>
        </div>

        {/* Stepper */}
        <Stepper current={currentStep} />

        {/* Gap 5: Family context bar — visible on all steps */}
        <div
          data-testid="camps-register-family-context"
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-white px-4 py-2.5 text-sm shadow-sm"
        >
          <span className="font-semibold text-iron-grey">
            {data.familyName ?? 'Your Family'}
          </span>
          {selectedCamperNames.length > 0 ? (
            <>
              <span className="text-text-secondary">&bull;</span>
              <span className="text-text-secondary">Registering:</span>
              {selectedCamperNames.map((name) => (
                <span
                  key={name}
                  className="rounded-full bg-[var(--primary)]/10 px-2.5 py-0.5 text-xs font-medium text-[var(--primary)]"
                >
                  {name}
                </span>
              ))}
            </>
          ) : (
            <>
              <span className="text-text-secondary">&bull;</span>
              <span className="text-text-secondary">No campers selected</span>
            </>
          )}
        </div>

        {/* Main content + sidebar layout */}
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Step 1: Camper selection */}
            {currentStep === 'campers' ? (
              <section data-testid="camps-step-campers" className="rounded-xl border border-border bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-iron-grey">Select Campers</h2>
                <p className="mb-4 text-sm text-text-secondary">
                  Choose which of your eligible children to register for this camp.
                </p>
                {eligibleStudents.length === 0 ? (
                  <p className="py-8 text-center text-sm text-text-secondary">
                    No eligible students found for this program.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {eligibleStudents.map((student) => {
                      const id = student.studentId ?? '';
                      const checked = selectedStudentIds.has(id);
                      return (
                        <label
                          key={id}
                          className={`flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors ${
                            checked
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-border hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleStudent(id)}
                            className="h-4 w-4 rounded border-border text-[var(--primary)] focus:ring-[var(--primary)]"
                          />
                          <div>
                            <p className="font-medium text-iron-grey">{student.name ?? '--'}</p>
                            {student.grade ? (
                              <p className="text-sm text-text-secondary">{student.grade}</p>
                            ) : null}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    data-testid="camps-register-save-draft"
                    disabled={!canSaveDraft || isSavingDraft}
                    onClick={() => { void handleSaveDraft(); }}
                    className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-iron-grey transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSavingDraft ? 'Saving...' : draftSaved ? 'Draft Saved' : 'Save Draft'}
                  </button>
                  <button
                    type="button"
                    disabled={!canAdvanceFromCampers}
                    onClick={() => setCurrentStep('schedule')}
                    className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    Continue
                  </button>
                </div>
              </section>
            ) : null}

            {/* Step 2: Session selection */}
            {currentStep === 'schedule' ? (
              <section data-testid="camps-step-schedule" className="rounded-xl border border-border bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-iron-grey">Choose Schedule</h2>
                <p className="mb-4 text-sm text-text-secondary">
                  Select the sessions or weeks for your camper{selectedStudentIds.size > 1 ? 's' : ''}.
                </p>
                {sessions.length === 0 ? (
                  <p className="py-8 text-center text-sm text-text-secondary">
                    No sessions are currently available.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {sessions.map((session) => {
                      const id = session.sessionId ?? '';
                      const checked = selectedSessionIds.has(id);
                      const spotsLeft = session.spotsRemaining ?? (session.capacity != null && session.enrolled != null ? session.capacity - session.enrolled : null);
                      return (
                        <label
                          key={id}
                          className={`flex cursor-pointer items-center justify-between gap-3 rounded-lg border p-4 transition-colors ${
                            checked
                              ? 'border-[var(--primary)] bg-[var(--primary)]/5'
                              : 'border-border hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleSession(id)}
                              className="h-4 w-4 rounded border-border text-[var(--primary)] focus:ring-[var(--primary)]"
                            />
                            <div>
                              <p className="font-medium text-iron-grey">{session.label ?? '--'}</p>
                              {session.startDate || session.endDate ? (
                                <p className="text-sm text-text-secondary">
                                  {session.startDate ?? ''} &ndash; {session.endDate ?? ''}
                                </p>
                              ) : null}
                            </div>
                          </div>
                          {spotsLeft != null ? (
                            <div className="flex items-center gap-1.5">
                              <span
                                className="h-2 w-2 rounded-full"
                                style={{
                                  backgroundColor:
                                    spotsLeft > 5
                                      ? 'var(--aquamarine)'
                                      : spotsLeft > 0
                                        ? 'var(--coral)'
                                        : 'var(--strawberry)',
                                }}
                              />
                              <span className="text-sm text-text-secondary">
                                {spotsLeft > 0
                                  ? `${spotsLeft} spot${spotsLeft !== 1 ? 's' : ''} left`
                                  : 'Full'}
                              </span>
                            </div>
                          ) : null}
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep('campers')}
                    className="text-sm font-medium text-[var(--primary)] hover:underline"
                  >
                    Back
                  </button>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      data-testid="camps-register-save-draft"
                      disabled={!canSaveDraft || isSavingDraft}
                      onClick={() => { void handleSaveDraft(); }}
                      className="rounded-lg border border-border px-4 py-2.5 text-sm font-semibold text-iron-grey transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isSavingDraft ? 'Saving...' : draftSaved ? 'Draft Saved' : 'Save Draft'}
                    </button>
                    <button
                      type="button"
                      disabled={!canAdvanceFromSchedule}
                      onClick={() => setCurrentStep('review')}
                      className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </section>
            ) : null}

            {/* Step 3: Review & Pay */}
            {currentStep === 'review' ? (
              <section data-testid="camps-step-review" className="rounded-xl border border-border bg-white p-5 shadow-sm">
                <h2 className="mb-4 text-lg font-semibold text-iron-grey">Review & Submit</h2>

                {/* Selected campers */}
                <div className="mb-4">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.05em] text-text-secondary">
                    Campers
                  </h3>
                  <div className="space-y-1">
                    {eligibleStudents
                      .filter((s) => selectedStudentIds.has(s.studentId ?? ''))
                      .map((s) => (
                        <p key={s.studentId} className="text-sm text-iron-grey">
                          {s.name}{s.grade ? ` (${s.grade})` : ''}
                        </p>
                      ))}
                  </div>
                </div>

                {/* Selected sessions */}
                <div className="mb-4">
                  <h3 className="mb-2 text-sm font-semibold uppercase tracking-[0.05em] text-text-secondary">
                    Sessions
                  </h3>
                  <div className="space-y-1">
                    {sessions
                      .filter((s) => selectedSessionIds.has(s.sessionId ?? ''))
                      .map((s) => (
                        <p key={s.sessionId} className="text-sm text-iron-grey">
                          {s.label}
                          {s.startDate ? ` (${s.startDate} - ${s.endDate ?? ''})` : ''}
                        </p>
                      ))}
                  </div>
                </div>

                {/* Pricing summary (inline for mobile) */}
                <div className="mb-6 rounded-lg border border-border bg-background-light p-4 lg:hidden">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.05em] text-text-secondary">
                    Pricing Summary
                  </h3>
                  {(data.pricingSummary?.lines ?? []).map((line, idx) => (
                    <div key={line.label ?? idx} className="flex items-center justify-between text-sm">
                      <span className="text-text-secondary">{line.label ?? '--'}</span>
                      <span className="font-medium text-iron-grey">{formatCurrency(line.amountCents)}</span>
                    </div>
                  ))}
                  <div className="mt-3 border-t border-border pt-3">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-iron-grey">Total</span>
                      <span className="text-lg font-semibold text-iron-grey">
                        {formatCurrency(estimatedTotalCents)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Waiver */}
                <div className="mb-6">
                  <label className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={waiverAccepted}
                      onChange={(e) => setWaiverAccepted(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-border text-[var(--primary)] focus:ring-[var(--primary)]"
                    />
                    <span className="text-sm text-iron-grey">
                      I acknowledge and accept the camp policies, waivers, and terms for this registration.
                    </span>
                  </label>
                </div>

                {/* Payment readiness */}
                {data.paymentReadiness && !data.paymentReadiness.ready ? (
                  <div className="mb-6 rounded-lg border border-[var(--coral)] bg-white p-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-[var(--coral)]" aria-hidden="true" />
                      <p className="text-sm text-iron-grey">
                        {data.paymentReadiness.reason ?? 'Payment method required before submitting.'}
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* Submit error */}
                {submitError ? (
                  <div className="mb-6 rounded-lg border border-[var(--strawberry)] bg-white p-4">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-4 w-4 text-[var(--strawberry)]" aria-hidden="true" />
                      <p className="text-sm text-iron-grey">{submitError}</p>
                    </div>
                  </div>
                ) : null}

                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep('schedule')}
                    className="text-sm font-medium text-[var(--primary)] hover:underline"
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    disabled={!canSubmit || isSubmitting}
                    onClick={() => { void handleSubmit(); }}
                    className="rounded-lg bg-[var(--primary)] px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Registration'}
                  </button>
                </div>
              </section>
            ) : null}
          </div>

          {/* Desktop pricing sidebar */}
          <div className="hidden lg:block">
            <div className="sticky top-6">
              <PricingSidebar summary={data.pricingSummary ?? { totalCents: estimatedTotalCents, lines: [] }} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
